// Billing Subscriptions Service (purchase groups + cancellation)
// ---------------------------------------------------------------
// Client Owner-only. A "license group" is ONE purchase: a single checkout
// created one Stripe subscription covering N app licenses. That is the unit the
// customer can cancel, because it is the unit Stripe bills.
//
// No Stripe id ever reaches the browser: licenseGroupId is a NIMIS id that the
// backend resolves to the subscription over the caller's own tenant only. The
// same reason the checkout surface never exposes a Stripe price id.
//
// Cancellation is always effective at the END of the paid period and is
// reversible until then — see models/subscription_cancellation.py for why.

import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";
import { getGraphData } from "@/utils/transform";

// Mirror of the BE projection (billingmanagedatasource.list_billing_subscriptions).
export interface LicenseSubscriptionGroup {
  licenseGroupId: string;
  planId: string | null;
  planType: "one_off" | "subscription" | null;
  quantity: number;
  // The licenses in this purchase, so the UI can show exactly which apps a
  // cancellation would affect (joined against the entitlement list it holds).
  entitlementIds: string[];
  boundWebAppIds: string[];
  unassignedCount: number;
  status: string | null;
  currentPeriodEnd: number | null;
  // Confirmed by Stripe via the webhook — never optimistic client state, so a
  // failed cancel can't leave the UI claiming a subscription is ending.
  cancelAtPeriodEnd: boolean;
  cancelsAt: number | null;
  // Set when a PARTIAL reduction is scheduled: the quantity that will remain.
  pendingQuantity: number | null;
  pendingCancelCount: number;
  // False for tiers that cannot cancel self-serve (enterprise is contracted
  // offline). The BE enforces this independently.
  canCancel: boolean;
}

export interface LicenseSubscriptionChangeResult {
  cancelsAt: number | null;
  cancelledCount: number;
  // True when the cancellation leaves nothing live, closing the account on that
  // date. The UI must confirm this differently from an ordinary cancellation.
  closesAccount: boolean;
  error: string | null;
}

const LIST_BILLING_SUBSCRIPTIONS = `query ListBillingSubscriptions {
  listBillingSubscriptions {
    licenseGroupId
    planId
    planType
    quantity
    entitlementIds
    boundWebAppIds
    unassignedCount
    status
    currentPeriodEnd
    cancelAtPeriodEnd
    cancelsAt
    pendingQuantity
    pendingCancelCount
    canCancel
  }
}`;

const CANCEL_LICENSE_SUBSCRIPTION = `mutation CancelLicenseSubscription($input: CancelLicenseSubscriptionInput!) {
  cancelLicenseSubscription(input: $input) {
    cancelsAt
    cancelledCount
    closesAccount
    error
  }
}`;

const RESUME_LICENSE_SUBSCRIPTION = `mutation ResumeLicenseSubscription($input: ResumeLicenseSubscriptionInput!) {
  resumeLicenseSubscription(input: $input) {
    cancelsAt
    cancelledCount
    closesAccount
    error
  }
}`;

const graphqlOperation = (query: any, variables?: any) => {
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

interface GraphQLResponse {
  data?: Record<string, unknown>;
  errors?: Array<{ message?: string }>;
}

export async function fetchBillingSubscriptions(): Promise<LicenseSubscriptionGroup[]> {
  const response = (await API.graphql(
    graphqlOperation(LIST_BILLING_SUBSCRIPTIONS)
  )) as GraphQLResponse;
  const groups = getGraphData(response, "listBillingSubscriptions");
  return Array.isArray(groups) ? (groups as LicenseSubscriptionGroup[]) : [];
}

function toChangeResult(
  response: GraphQLResponse,
  field: string,
  fallbackError: string,
): LicenseSubscriptionChangeResult {
  const result = getGraphData(response, field) as LicenseSubscriptionChangeResult | null;
  if (!result) {
    return { cancelsAt: null, cancelledCount: 0, closesAccount: false, error: fallbackError };
  }
  return result;
}

/** Cancel the named licenses in one purchase group, effective at period end.
 *  Never throws — a rejected resolver comes back as an error code so callers
 *  have one path to handle. */
export async function cancelLicenseSubscription(
  licenseGroupId: string,
  entitlementIds: string[],
): Promise<LicenseSubscriptionChangeResult> {
  try {
    const response = (await API.graphql(
      graphqlOperation(CANCEL_LICENSE_SUBSCRIPTION, {
        input: { licenseGroupId, entitlementIds },
      })
    )) as GraphQLResponse;
    return toChangeResult(response, "cancelLicenseSubscription", "CANCEL_FAILED");
  } catch (cancelError: any) {
    const message =
      cancelError?.errors?.[0]?.message || cancelError?.message || "CANCEL_FAILED";
    return { cancelsAt: null, cancelledCount: 0, closesAccount: false, error: message };
  }
}

/** Undo a pending cancellation before it takes effect. */
export async function resumeLicenseSubscription(
  licenseGroupId: string,
): Promise<LicenseSubscriptionChangeResult> {
  try {
    const response = (await API.graphql(
      graphqlOperation(RESUME_LICENSE_SUBSCRIPTION, { input: { licenseGroupId } })
    )) as GraphQLResponse;
    return toChangeResult(response, "resumeLicenseSubscription", "RESUME_FAILED");
  } catch (resumeError: any) {
    const message =
      resumeError?.errors?.[0]?.message || resumeError?.message || "RESUME_FAILED";
    return { cancelsAt: null, cancelledCount: 0, closesAccount: false, error: message };
  }
}

export function subscriptionChangeErrorMessage(code: string | null | undefined): string {
  switch (code) {
    case "FORBIDDEN":
      return "Only an account owner can change subscriptions.";
    case "ACCOUNT_NOT_ACTIVE":
      return "Your account isn't active. Contact support if you believe this is an error.";
    case "LICENSE_GROUP_NOT_FOUND":
      return "That subscription couldn't be found. Refresh the page and try again.";
    case "NOT_CANCELLABLE":
      return "This subscription is managed by your account manager. Contact support to make changes.";
    case "INVALID_SELECTION":
      return "That selection is no longer valid. Refresh the page and try again.";
    case "RESUME_FAILED":
      return "We couldn't restore the subscription. Please try again.";
    default:
      return "We couldn't change the subscription. Please try again.";
  }
}

// ---------------------------------------------------------------------------
// Account-level billing context, receipts, and the card on file
// ---------------------------------------------------------------------------
// Grouped with the subscription surface because they answer the same question
// ("what am I paying, on what card, and what have I been charged?") and are
// rendered by the same page.

export interface PaymentMethodSummary {
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
}

export interface BillingOverview {
  // Days a closed account's data is kept before permanent deletion. Read
  // server-side from the same SSM parameter the purge job uses, so the closure
  // confirmation cannot promise a window the purge won't honour — which is why
  // this is never hardcoded in the UI.
  dataRetentionDays: number | null;
  paymentMethod: PaymentMethodSummary | null;
  nextChargeAt: number | null;
  canManagePaymentMethod: boolean;
}

export interface BillingInvoice {
  invoiceId: string | null;
  number: string | null;
  createdAt: number | null;
  amountPaid: number | null;   // minor units (e.g. cents)
  currency: string | null;
  status: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export interface PaymentMethodSetupSession {
  clientSecret: string | null;
  publishableKey: string | null;
  error: string | null;
}

const GET_BILLING_OVERVIEW = `query GetBillingOverview {
  getBillingOverview {
    dataRetentionDays
    nextChargeAt
    canManagePaymentMethod
    paymentMethod {
      brand
      last4
      expMonth
      expYear
    }
  }
}`;

const LIST_BILLING_INVOICES = `query ListBillingInvoices {
  listBillingInvoices {
    invoiceId
    number
    createdAt
    amountPaid
    currency
    status
    hostedInvoiceUrl
    invoicePdf
  }
}`;

const CREATE_PAYMENT_METHOD_SETUP_SESSION = `mutation CreatePaymentMethodSetupSession {
  createPaymentMethodSetupSession {
    clientSecret
    publishableKey
    error
  }
}`;

export async function fetchBillingOverview(): Promise<BillingOverview | null> {
  const response = (await API.graphql(
    graphqlOperation(GET_BILLING_OVERVIEW)
  )) as GraphQLResponse;
  return (getGraphData(response, "getBillingOverview") as BillingOverview | null) || null;
}

export async function fetchBillingInvoices(): Promise<BillingInvoice[]> {
  const response = (await API.graphql(
    graphqlOperation(LIST_BILLING_INVOICES)
  )) as GraphQLResponse;
  const invoices = getGraphData(response, "listBillingInvoices");
  return Array.isArray(invoices) ? (invoices as BillingInvoice[]) : [];
}

/** Open a setup-mode embedded Checkout Session to replace the card on file.
 *  Card details go straight from the browser to Stripe — they never reach us. */
export async function createPaymentMethodSetupSession(): Promise<PaymentMethodSetupSession> {
  try {
    const response = (await API.graphql(
      graphqlOperation(CREATE_PAYMENT_METHOD_SETUP_SESSION)
    )) as GraphQLResponse;
    const result = getGraphData(response, "createPaymentMethodSetupSession") as
      | PaymentMethodSetupSession
      | null;
    if (!result) {
      return { clientSecret: null, publishableKey: null, error: "SETUP_SESSION_FAILED" };
    }
    return result;
  } catch (setupError: any) {
    const message =
      setupError?.errors?.[0]?.message || setupError?.message || "SETUP_SESSION_FAILED";
    return { clientSecret: null, publishableKey: null, error: message };
  }
}

export function paymentMethodErrorMessage(code: string | null | undefined): string {
  switch (code) {
    case "FORBIDDEN":
      return "Only an account owner can change the payment method.";
    case "ACCOUNT_NOT_ACTIVE":
      return "Your account isn't active. Contact support if you believe this is an error.";
    case "NO_BILLING_ACCOUNT":
      return "There's no billing account yet. Buy a license first, then you can manage the card here.";
    default:
      return "We couldn't open the card form. Please try again.";
  }
}

/** "Visa •••• 4242" — enough to recognise the card, never enough to use it. */
export function formatPaymentMethod(paymentMethod: PaymentMethodSummary | null): string | null {
  if (!paymentMethod?.last4) return null;
  const brand = paymentMethod.brand
    ? paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)
    : "Card";
  return `${brand} •••• ${paymentMethod.last4}`;
}

/** "Expires 04/2027", or null when Stripe didn't report an expiry. */
export function formatCardExpiry(paymentMethod: PaymentMethodSummary | null): string | null {
  if (!paymentMethod?.expMonth || !paymentMethod?.expYear) return null;
  return `Expires ${String(paymentMethod.expMonth).padStart(2, "0")}/${paymentMethod.expYear}`;
}

/** True when the card is past, or within a month of, its expiry. A failed
 *  renewal on a subscription of this size is expensive to recover, so it is
 *  worth warning before the charge fails rather than after. */
export function isCardExpiringSoon(
  paymentMethod: PaymentMethodSummary | null,
  now: Date = new Date(),
): boolean {
  if (!paymentMethod?.expMonth || !paymentMethod?.expYear) return false;
  // A card is valid through the END of its expiry month.
  const expiryEnd = new Date(paymentMethod.expYear, paymentMethod.expMonth, 1);
  const warnFrom = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return expiryEnd <= warnFrom;
}
